import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import {
  CardDetailModal,
  CardsGrid,
  DeckSelector,
  FilterComponent,
  SearchBar,
} from "../components";
import { useSelector } from "react-redux";
import {
  CARD_TYPES,
  userCardsQuery,
  userDecksQuery,
  userSampleCardsQuery,
  S3_URL,
} from "../utils";
import { redirect } from "react-router-dom";
import {
  setAllCards,
  setPurchasedDecks,
  setSelectedDeck,
  setSelectedCard,
  setFilterToCards,
} from "../features/cards/cardsSlice";
import { deckTypes, DECK_PURCHASE_CARD_TITLE, fetchImage } from "../utils";
import { openCard, closeCard } from "../features/ui/uiSlice";
import { Typography } from "@mui/material";

export const loader =
  (store, queryClient) =>
  async ({ request }) => {
    const params = Object.fromEntries([
      ...new URL(request.url).searchParams.entries(),
    ]);
    if (params.deck && CARD_TYPES.includes(params.deck)) {
      const deckTitle = DECK_PURCHASE_CARD_TITLE[params.deck];
      store.dispatch(setSelectedDeck(deckTitle));
    }

    // In HomePage.js loader
    // Add a check for the referrer or a specific query parameter
    const isFromRedeemPage = request.headers
      .get("Referer")
      ?.includes("/redeem-codes");
    if (isFromRedeemPage) {
      // Force refetch data instead of using cached data
      await queryClient.refetchQueries(["userCards", userId]);
      await queryClient.refetchQueries(["userDecks", userId]);
      await queryClient.refetchQueries(["userSampleCards", userId]);
    }

    
    const user = store.getState().userState.user;
    if (!user) {
      return redirect("/signin");
    }
    const userId = user.user_id;
    let cardList = [];
    let purchasedDecks = [];

    try {
      const userCardsResponse = await queryClient.ensureQueryData(
        userCardsQuery(userId, "EN")
      );
      const userDecksResponse = await queryClient.ensureQueryData(
        userDecksQuery(userId, "EN")
      );

      const userCardsSampleResponse = await queryClient.ensureQueryData(
        userSampleCardsQuery(userId, "EN")
      );

      const userCards = userCardsResponse.cards;
      const userDecks = userDecksResponse.userDecks;
      const exampleCards = userCardsSampleResponse.samUserCards;

      const purchasedDeckCodes = new Set(
        userDecks.map((deck) => deck.deck_code)
      );
      const filteredUserCards = userCards.filter((card) =>
        purchasedDeckCodes.has(card.card_deck_code)
      );
      const filteredExampleCards = exampleCards.filter(
        (card) => !purchasedDeckCodes.has(card.card_deck_code)
      );
      const resultCards = [...filteredUserCards, ...filteredExampleCards];
      const uniqueResultCards = Array.from(
        new Map(resultCards.map((card) => [card.card_id, card])).values()
      );

      purchasedDecks = userDecksResponse.userDecks;
      cardList = uniqueResultCards;

      // Store the data in localStorage for offline use
      localStorage.setItem('offlineCards', JSON.stringify(cardList));
      localStorage.setItem('offlineDecks', JSON.stringify(purchasedDecks));
    } catch (error) {
      console.log('Network error, trying to use offline data...');
      // If network error, try to use offline data
      const offlineCards = localStorage.getItem('offlineCards');
      const offlineDecks = localStorage.getItem('offlineDecks');
      
      if (offlineCards && offlineDecks) {
        cardList = JSON.parse(offlineCards);
        purchasedDecks = JSON.parse(offlineDecks);
      } else {
        console.error('No offline data available');
        return {};
      }
    }

    store.dispatch(closeCard());
    store.dispatch(setPurchasedDecks(purchasedDecks));
    store.dispatch(setAllCards(cardList));
    const activeFilter = store.getState().cardsState.areCardsFiltered;
    if (activeFilter !== "not filtered") {
      store.dispatch(setFilterToCards(activeFilter));
    }

    // Random Card
    const url = new URL(request.url);
    const queryParams = new URLSearchParams(url.search);
    const shuffleCard = queryParams?.get("shuffleCard");
    if (!!shuffleCard) {
      store.dispatch(openCard());
      let actualDeck = JSON.parse(localStorage.getItem("decks")).actualDeck[0]
        .card_deck_code;
      const matchingCards = cardList.filter(
        (card) => card.card_deck_code === actualDeck
      );
      if (matchingCards.length > 0) {
        const randomCard =
          matchingCards[Math.floor(Math.random() * matchingCards.length)];
        store.dispatch(setSelectedCard(randomCard));
      }
    }
    return null;
  };

const HomePage = () => {
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const { isSearchBarOpen } = useSelector((state) => state.uiState);
  const selectedDeck = useSelector((state) => state.cardsState.selectedDeck);
  const purchasedDecksCodes = useSelector(
    (state) => state.cardsState.purchasedDecks
  );
  const allCards = useSelector((state) => state.cardsState.allCards);
  const deckProperties = deckTypes?.filter(
    (deck) => deck.title === selectedDeck
  )?.[0];
  const selectedDeckCode = Object.keys(DECK_PURCHASE_CARD_TITLE).find(
    (key) => DECK_PURCHASE_CARD_TITLE[key] === selectedDeck
  );
  const isShowMore = !purchasedDecksCodes?.includes(selectedDeckCode);
  const cards = useSelector((state) => state.cardsState.allDecks);

  useEffect(() => {
    const preloadImages = async () => {
      if (!cards) return;

      // Check if images were already preloaded
      const preloadStatus = localStorage.getItem('imagesPreloaded');
      if (preloadStatus === 'true') {
        return;
      }

      setIsPreloading(true);
      setPreloadProgress(0);

      // Get all unique image URLs from all decks
      const imageUrls = new Set();
      
      // Iterate through each deck (CHA, DEC, EDU, etc.)
      Object.values(cards).forEach(deckCards => {
        deckCards.forEach(card => {
          // Add front image URL
          if (card.img_card) {
            const frontUrl = card.img_card.includes(S3_URL) 
              ? card.img_card 
              : S3_URL + card.img_card;
            imageUrls.add(frontUrl);
          }
          
          // Add back image URL if it exists
          if (card.img_card_back) {
            const backUrl = card.img_card_back.includes(S3_URL)
              ? card.img_card_back
              : S3_URL + card.img_card_back;
            imageUrls.add(backUrl);
          }
        });
      });

      const totalUniqueImages = imageUrls.size;
      setTotalImages(totalUniqueImages);
      
      // Preload all unique images with delay
      let loadedCount = 0;
      for (const imageUrl of imageUrls) {
        try {
          // Create a new image object to preload
          const img = new Image();
          
          // Create a promise that resolves when the image loads or errors
          const loadPromise = new Promise((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => {
              console.warn(`Failed to preload image: ${imageUrl}`);
              resolve(); // Resolve anyway to continue with next image
            };
          });

          // Set the image source to start loading
          img.src = imageUrl;
          
          // Wait for the image to load or error
          await loadPromise;
          
          loadedCount++;
          setPreloadProgress(Math.round((loadedCount / totalUniqueImages) * 100));
          
          // Add 100ms delay between requests
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.error(`Failed to preload image ${imageUrl}:`, error);
          // Continue with next image even if this one fails
          loadedCount++;
          setPreloadProgress(Math.round((loadedCount / totalUniqueImages) * 100));
        }
      }

      // Mark preloading as complete in localStorage
      localStorage.setItem('imagesPreloaded', 'true');
      setIsPreloading(false);
    };

    preloadImages();
  }, [cards, purchasedDecksCodes]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "10px 20px",
        position: "relative",
      }}
      className="homePage"
    >
      {isPreloading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Typography variant="h6" color="white" sx={{ mb: 2, textAlign: 'center' }}>
            Preparing your Virtue Cards experience...
          </Typography>
          <Typography variant="body1" color="white" sx={{ mb: 1, textAlign: 'center' }}>
            Loading your cards with love and care
          </Typography>
          <Typography variant="h4" color="white" sx={{ fontWeight: 'bold' }}>
            {preloadProgress}%
          </Typography>
        </Box>
      )}

      <FilterComponent />
      {isSearchBarOpen && <SearchBar />}
      <DeckSelector deckTypes={deckTypes} />
      <CardsGrid
        deckProperties={deckProperties}
        isShowMore={isShowMore}
        deckCode={selectedDeckCode}
      />
      <CardDetailModal deckProperties={deckProperties} />
    </Box>
  );
};

export default HomePage;
